import { Box, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";

import { articleTypeLabel, formatLearnLabel } from "./learnLabels";

function firstSentence(text = "") {
  const trimmed = text.trim();
  if (!trimmed) return "";
  const match = trimmed.match(/^.+?[.!?](?=\s|$)/);
  return match ? match[0] : trimmed;
}

export default function RelatedLearnArticles({ relatedArticles }) {
  const items = Array.isArray(relatedArticles) ? relatedArticles : [];
  if (!items.length) return null;
  const visibleItems = items.slice(0, 3);

  return (
    <Box sx={{ mt: { xs: 5, md: 6 } }}>
      <Typography variant="h6" component="h2" sx={{ fontWeight: 800, mb: 2 }}>
        Related Articles
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
          gap: 1.5,
        }}
      >
        {visibleItems.map((relationship) => {
          const article = relationship.article || {};
          const metadata = [articleTypeLabel(article.article_type), formatLearnLabel(article.category)].filter(Boolean);
          const description = firstSentence(article.description);
          return (
            <Box
              key={`${relationship.relationship_type}-${article.slug}`}
              component={RouterLink}
              to={`/learn/${article.slug}`}
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: 255,
                p: { xs: 2.5, md: 3 },
                color: "text.primary",
                textDecoration: "none",
                backgroundColor: "#fff",
                border: "1px solid rgba(15, 23, 42, 0.08)",
                borderRadius: 2,
                boxShadow: 1,
                position: "relative",
                transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                "&:before": {
                  content: '""',
                  position: "absolute",
                  left: 24,
                  right: 24,
                  top: 0,
                  height: 3,
                  backgroundColor: "primary.main",
                  borderRadius: "0 0 3px 3px",
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 2,
                  borderColor: "rgba(15, 23, 42, 0.2)",
                },
                "&:hover .related-learn-title": { color: "primary.main" },
                "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 3 },
              }}
            >
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 850, letterSpacing: 0.5, textTransform: "uppercase" }}>
                {articleTypeLabel(article.article_type) || "Learn"}
              </Typography>
              <Typography className="related-learn-title" sx={{ mt: 1.25, fontSize: 19, fontWeight: 850, lineHeight: 1.25 }}>
                {article.title}
              </Typography>
              {description ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25, lineHeight: 1.65, flex: 1 }}>
                  {description}
                </Typography>
              ) : null}
              {metadata.length ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, fontSize: 12.5 }}>
                  {metadata.join(" - ")}
                </Typography>
              ) : null}
              <Typography variant="caption" sx={{ mt: 1.75, color: "text.secondary", fontWeight: 750 }}>
                View article &gt;
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
